/**
 * useCytoscapeLOD Hook
 *
 * Level of Detail (LOD) management with zoom-to-expand functionality.
 * Automatically expands compound nodes when zoomed in, collapses when zoomed out.
 */

import type { Core, NodeSingular } from 'cytoscape'
import { useCallback, useEffect, useRef, useState } from 'react'
import { addEdgesSafely, safeBatch } from './safe-batch'
import { type GraphEdge, type GraphNode, LOD_LEVELS } from './types'

export interface LODState {
  currentLevel: 'minimal' | 'basic' | 'standard' | 'detailed' | 'full'
  expandedGroups: Set<string>
  loadingGroups: Set<string>
}

export interface UseCytoscapeLODOptions {
  cy: Core | null
  enabled?: boolean
  onLoadGroupChildren?: (groupId: string) => Promise<{ nodes: GraphNode[]; edges: GraphEdge[] }>
  autoExpandOnZoom?: boolean
  autoCollapseOnZoom?: boolean
}

export interface UseCytoscapeLODReturn {
  lodState: LODState
  currentLODLevel: string
  // Actions
  expandGroup: (groupId: string) => Promise<void>
  collapseGroup: (groupId: string) => void
  setLODLevel: (level: 'minimal' | 'basic' | 'standard' | 'detailed' | 'full') => void
  isGroupExpanded: (groupId: string) => boolean
  isGroupLoading: (groupId: string) => boolean
}

function getZoomLODLevel(zoom: number): 'minimal' | 'basic' | 'standard' | 'detailed' | 'full' {
  if (zoom <= LOD_LEVELS.minimal.maxZoom) return 'minimal'
  if (zoom <= LOD_LEVELS.basic.maxZoom) return 'basic'
  if (zoom <= LOD_LEVELS.standard.maxZoom) return 'standard'
  if (zoom <= LOD_LEVELS.detailed.maxZoom) return 'detailed'
  return 'full'
}

export function useCytoscapeLOD({
  cy,
  enabled = true,
  onLoadGroupChildren,
  autoExpandOnZoom = true,
  autoCollapseOnZoom = true,
}: UseCytoscapeLODOptions): UseCytoscapeLODReturn {
  const [lodState, setLODState] = useState<LODState>({
    currentLevel: 'standard',
    expandedGroups: new Set(),
    loadingGroups: new Set(),
  })

  const prevZoomRef = useRef<number>(1)
  const zoomTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Update visual styles based on LOD level
  const applyLODStyles = useCallback(
    (level: 'minimal' | 'basic' | 'standard' | 'detailed' | 'full') => {
      if (!cy) return

      safeBatch(cy, () => {
        const config = LOD_LEVELS[level]

        // Show/hide labels based on LOD
        if (config.showLabels) {
          cy.nodes().removeClass('lod-hide-labels')
        } else {
          cy.nodes().addClass('lod-hide-labels')
        }

        // Show/hide edge labels
        if (config.showEdgeLabels) {
          cy.edges().removeClass('lod-hide-edge-labels')
          cy.edges().addClass('lod-show-edge-labels')
        } else {
          cy.edges().addClass('lod-hide-edge-labels')
          cy.edges().removeClass('lod-show-edge-labels')
        }

        // Show/hide icons
        if (config.showIcons) {
          cy.nodes().removeClass('lod-hide-icons')
        } else {
          cy.nodes().addClass('lod-hide-icons')
        }

        // Show entity type labels when zoomed in enough (standard+)
        if (config.showLabels) {
          cy.nodes().addClass('lod-show-type-label')
        } else {
          cy.nodes().removeClass('lod-show-type-label')
        }
      })
    },
    [cy]
  )

  // Set LOD level manually
  const setLODLevel = useCallback(
    (level: 'minimal' | 'basic' | 'standard' | 'detailed' | 'full') => {
      setLODState(prev => ({ ...prev, currentLevel: level }))
      applyLODStyles(level)
    },
    [applyLODStyles]
  )

  // Expand a group node to show its children
  const expandGroup = useCallback(
    async (groupId: string) => {
      if (!cy || !onLoadGroupChildren) return

      const groupNode = cy.$id(groupId)
      if (groupNode.length === 0) return

      // Mark as loading
      setLODState(prev => ({
        ...prev,
        loadingGroups: new Set(prev.loadingGroups).add(groupId),
      }))

      groupNode.addClass('loading')

      try {
        // Load children from backend
        const { nodes, edges } = await onLoadGroupChildren(groupId)

        if (!cy) return // Check cy still exists

        safeBatch(cy, () => {
          // Add child nodes with parent reference
          nodes.forEach(node => {
            if (cy.$id(node.id).length === 0) {
              cy.add({
                group: 'nodes',
                data: {
                  id: node.id,
                  parent: groupId,
                  typeLabel: node.typeLabel,
                  name: node.name,
                  displayName: node.displayName || node.name,
                  labels: node.labels,
                  properties: node.properties,
                },
              })
            }
          })

          // Add edges (addEdgesSafely drops any whose endpoint node is absent)
          const newEdges = edges
            .filter(edge => cy.$id(edge.id).length === 0)
            .map(edge => ({
              group: 'edges' as const,
              data: {
                id: edge.id,
                source: edge.source,
                target: edge.target,
                type: edge.type,
                properties: edge.properties,
              },
            }))
          addEdgesSafely(cy, newEdges)

          // Mark group as expanded
          groupNode.addClass('expanded')
          groupNode.removeClass('loading')
        })

        // Run layout for new nodes
        const childNodes = cy.nodes(`[parent = "${groupId}"]`)
        if (childNodes.length > 0) {
          childNodes
            .layout({
              name: 'cose',
              animate: true,
              animationDuration: 300,
              fit: false,
              boundingBox: groupNode.boundingBox(),
            })
            .run()
        }

        setLODState(prev => {
          const next = { ...prev }
          next.expandedGroups = new Set(prev.expandedGroups).add(groupId)
          next.loadingGroups = new Set(prev.loadingGroups)
          next.loadingGroups.delete(groupId)
          return next
        })
      } catch (error) {
        console.error(`Failed to expand group ${groupId}:`, error)
        groupNode.removeClass('loading')

        setLODState(prev => {
          const next = { ...prev }
          next.loadingGroups = new Set(prev.loadingGroups)
          next.loadingGroups.delete(groupId)
          return next
        })
      }
    },
    [cy, onLoadGroupChildren]
  )

  // Collapse a group node
  const collapseGroup = useCallback(
    (groupId: string) => {
      if (!cy) return

      const groupNode = cy.$id(groupId)
      if (groupNode.length === 0) return

      safeBatch(cy, () => {
        // Remove child nodes and their edges
        const children = cy.nodes(`[parent = "${groupId}"]`)
        const childEdges = children.connectedEdges()

        childEdges.remove()
        children.remove()

        groupNode.removeClass('expanded')
      })

      setLODState(prev => {
        const next = { ...prev }
        next.expandedGroups = new Set(prev.expandedGroups)
        next.expandedGroups.delete(groupId)
        return next
      })
    },
    [cy]
  )

  // Check if group is expanded
  const isGroupExpanded = useCallback(
    (groupId: string) => {
      return lodState.expandedGroups.has(groupId)
    },
    [lodState.expandedGroups]
  )

  // Check if group is loading
  const isGroupLoading = useCallback(
    (groupId: string) => {
      return lodState.loadingGroups.has(groupId)
    },
    [lodState.loadingGroups]
  )

  // Auto-expand/collapse based on zoom level
  useEffect(() => {
    if (!cy || !enabled) return

    const handleZoom = () => {
      const zoom = cy.zoom()
      const prevZoom = prevZoomRef.current

      // Debounce zoom handling
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current)
      }

      zoomTimeoutRef.current = setTimeout(() => {
        const newLevel = getZoomLODLevel(zoom)
        const oldLevel = lodState.currentLevel

        // Update LOD level if changed
        if (newLevel !== oldLevel) {
          setLODLevel(newLevel)
        }

        // Auto-expand groups when zooming in significantly
        if (autoExpandOnZoom && zoom > prevZoom && zoom > LOD_LEVELS.detailed.maxZoom) {
          // Find visible group nodes that aren't expanded
          const visibleGroups = cy.nodes('.group-node:visible').not('.expanded').not('.loading')

          // Get the center of the viewport
          const extent = cy.extent()
          const centerX = (extent.x1 + extent.x2) / 2
          const centerY = (extent.y1 + extent.y2) / 2

          // Find groups near the center
          visibleGroups.forEach((node: NodeSingular) => {
            const pos = node.position()
            const distance = Math.sqrt((pos.x - centerX) ** 2 + (pos.y - centerY) ** 2)

            // Expand if close to center
            if (distance < 200) {
              expandGroup(node.id())
            }
          })
        }

        // Auto-collapse groups when zooming out
        if (autoCollapseOnZoom && zoom < prevZoom && zoom < LOD_LEVELS.basic.maxZoom) {
          lodState.expandedGroups.forEach(groupId => {
            collapseGroup(groupId)
          })
        }

        prevZoomRef.current = zoom
      }, 150) // Debounce delay
    }

    cy.on('zoom', handleZoom)

    return () => {
      cy.off('zoom', handleZoom)
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current)
      }
    }
  }, [
    cy,
    enabled,
    autoExpandOnZoom,
    autoCollapseOnZoom,
    lodState.currentLevel,
    lodState.expandedGroups,
    setLODLevel,
    expandGroup,
    collapseGroup,
  ])

  // Double-click to expand/collapse
  useEffect(() => {
    if (!cy || !enabled) return

    const handleDoubleClick = (evt: { target: NodeSingular }) => {
      const node = evt.target

      // Check if it's a group node
      if (node.hasClass('group-node') || node.data('isGroup')) {
        if (isGroupExpanded(node.id())) {
          collapseGroup(node.id())
        } else {
          expandGroup(node.id())
        }
      }
    }

    cy.on('dbltap', 'node', handleDoubleClick)

    return () => {
      cy.off('dbltap', 'node', handleDoubleClick)
    }
  }, [cy, enabled, isGroupExpanded, expandGroup, collapseGroup])

  return {
    lodState,
    currentLODLevel: lodState.currentLevel,
    expandGroup,
    collapseGroup,
    setLODLevel,
    isGroupExpanded,
    isGroupLoading,
  }
}
